import { Col, Form, Row, DatePicker, InputNumber, Select, Table, Button, Input } from 'antd';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';

interface ProductInfoProps {
  product: any[];
  editable: boolean;
  form: any;
  lineData: any;
  follow_up_id: number | null;
  FollowUpData: any;
  serviceCharges?: any[];
  arrivalData?: any;
  departmentId?: number;
  importLineVarieties?: any[];
  serviceChargeOptions?: any[];
  department?: { id: number; name: string } | null;
}

const ProductInfo: React.FC<ProductInfoProps> = ({
  editable,
  form,
  arrivalData = [],
  departmentId,
  importLineVarieties = [],
  serviceChargeOptions = [],
  serviceCharges,
  department,
}) => {
  const [serviceChargeRows, setServiceChargeRows] = useState<any[]>([]);
  const [selectedServiceCharge, setSelectedServiceCharge] = useState<number | null>(null);
  const [serviceQty, setServiceQty] = useState<number>(1);

  useEffect(() => {
    form.setFieldsValue({
      department_id: departmentId,
      arrival_line_id: arrivalData?.id,
    });
  }, [departmentId, arrivalData, form]);

  useEffect(() => {
    if (serviceCharges?.length) {
      const mappedCharges = serviceCharges.map((item: any, index: number) => ({
        id: item.id ?? index,
        service_charge_id: item.service_charge_id,
        service_charge_name: item.service_charge?.name || item.service_charge_name || item.name,
        qty: item.qty,
      }));

      setServiceChargeRows(mappedCharges);

      // keep form synced
      form.setFieldsValue({
        service_charges: mappedCharges,
      });
    }
  }, [serviceCharges, form]);

  const handleAddServiceCharge = () => {
    if (!selectedServiceCharge) return;

    const selectedService = serviceChargeOptions.find((service: any) => service.value === selectedServiceCharge);

    const newRow = {
      service_charge_id: selectedServiceCharge,
      service_charge_name: selectedService?.label,
      qty: serviceQty,
    };

    const updatedRows = [...serviceChargeRows, newRow];

    setServiceChargeRows(updatedRows);

    // IMPORTANT: save into form
    form.setFieldsValue({
      service_charges: updatedRows,
    });

    setSelectedServiceCharge(null);
    setServiceQty(1);
  };

  const serviceChargeColumns = [
    {
      title: 'Service Charge',
      dataIndex: 'service_charge_name',
      key: 'service_charge_name',
    },
    {
      title: 'Qty',
      dataIndex: 'qty',
      key: 'qty',
    },
  ];

  return (
    <div style={{ width: '90%', marginLeft: '-24px', paddingLeft: '24px' }}>
      <Row gutter={[16, 16]}>
        <BaseCol xs={24} md={12}>
          <Form.Item label="Product">
            <BaseInput value={arrivalData?.import_line?.product?.name || ''} readOnly />
          </Form.Item>
        </BaseCol>
        <BaseCol xs={24} md={12}>
          <Form.Item label="Varieties">
            <BaseInput
              value={importLineVarieties
                .map((item) => item?.product_variety?.name)
                .filter(Boolean)
                .join(', ')}
              readOnly
            />
          </Form.Item>
        </BaseCol>

        {/* department_id */}
        <BaseCol xs={24} md={12}>
          {/* hidden submitted value */}
          <Form.Item name="department_id" hidden>
            <Input />
          </Form.Item>

          {/* displayed value */}
          <Form.Item label="Department">
            <BaseInput value={department?.name ?? '-'} readOnly />
          </Form.Item>
        </BaseCol>

        {/* expire_date */}
        <BaseCol xs={24} md={12}>
          <Form.Item label="Expire Date" name="expire_date">
            {editable ? (
              <DatePicker style={{ width: '100%' }} />
            ) : (
              <BaseInput
                value={
                  form.getFieldValue('expire_date')
                    ? moment(form.getFieldValue('expire_date')).format('YYYY-MM-DD')
                    : ''
                }
                readOnly
              />
            )}
          </Form.Item>
        </BaseCol>

        {/* arrival_line_id */}
        <BaseCol xs={24} md={12}>
          {/* hidden submitted value */}
          <Form.Item name="arrival_line_id" hidden>
            <Input />
          </Form.Item>

          {/* displayed value */}
          <Form.Item label="Arrival Line">
            <BaseInput value={arrivalData?.arrival?.code || ''} readOnly />
          </Form.Item>
        </BaseCol>

        {/* qty */}
        <BaseCol xs={24} md={12}>
          <Form.Item label="Qty" name="qty">
            {editable ? (
              <InputNumber min={0} style={{ width: '100%' }} />
            ) : (
              <BaseInput value={form.getFieldValue('qty')} readOnly />
            )}
          </Form.Item>
        </BaseCol>

        {/* start_date */}
        {/* start_date */}
        <BaseCol xs={24} md={12}>
          <Form.Item label="Start Date" name="start_date">
            {editable ? (
              <DatePicker style={{ width: '100%' }} />
            ) : (
              <BaseInput
                value={
                  form.getFieldValue('start_date') ? moment(form.getFieldValue('start_date')).format('YYYY-MM-DD') : ''
                }
                readOnly
              />
            )}
          </Form.Item>
        </BaseCol>

        {/* end_date */}
        <BaseCol xs={24} md={12}>
          <Form.Item label="End Date" name="end_date">
            {editable ? (
              <DatePicker style={{ width: '100%' }} />
            ) : (
              <BaseInput
                value={
                  form.getFieldValue('end_date') ? moment(form.getFieldValue('end_date')).format('YYYY-MM-DD') : ''
                }
                readOnly
              />
            )}
          </Form.Item>
        </BaseCol>

        {/* billing_qty */}
        <BaseCol xs={24} md={12}>
          <Form.Item label="Billing Qty" name="billing_qty">
            {editable ? (
              <InputNumber min={0} style={{ width: '100%' }} />
            ) : (
              <BaseInput value={form.getFieldValue('billing_qty')} readOnly />
            )}
          </Form.Item>
        </BaseCol>

        {/* billing_days */}
        <BaseCol xs={24} md={12}>
          <Form.Item label="Billing Days" name="billing_days">
            {editable ? (
              <InputNumber min={0} style={{ width: '100%' }} />
            ) : (
              <BaseInput value={form.getFieldValue('billing_days')} readOnly />
            )}
          </Form.Item>
        </BaseCol>
      </Row>

      {/* hidden service charges form field */}
      <Form.Item name="service_charges" hidden>
        <Input />
      </Form.Item>

      {/* SERVICE CHARGES SECTION */}
      <div style={{ marginTop: 32 }}>
        <h3>Service Charges</h3>

        {editable && (
          <Row gutter={[16, 16]} align="middle">
            <BaseCol xs={24} md={10}>
              <Form.Item label="Service Charge">
                <Select
                  placeholder="Select service charge"
                  value={selectedServiceCharge ?? undefined}
                  onChange={(value) => setSelectedServiceCharge(value)}
                  disabled={!editable}
                  style={{ width: '100%' }}
                >
                  {serviceChargeOptions.map((service: any) => (
                    <Select.Option key={service.value} value={service.value}>
                      {service.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </BaseCol>

            <BaseCol xs={24} md={6}>
              <Form.Item label="Qty">
                <InputNumber
                  min={1}
                  value={serviceQty}
                  onChange={(value: any) => setServiceQty(value)}
                  disabled={!editable}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </BaseCol>

            <BaseCol xs={24} md={6}>
              <Form.Item>
                <Button type="primary" onClick={handleAddServiceCharge} disabled={!editable}>
                  Add
                </Button>
              </Form.Item>
            </BaseCol>
          </Row>
        )}

        <Table
          dataSource={serviceChargeRows}
          columns={serviceChargeColumns}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: 'No service charges added' }}
          style={{ marginTop: 16 }}
        />
      </div>
    </div>
  );
};

export default ProductInfo;
